import _ from 'lodash'

// --- Shared primitives ---

const CLIENT_ID_NONE = 'NONE'

const PLAN_TYPES = {
  HRP: 'HRP',
  JAVELINA: 'Javelina'
}

const DIRECTORY_TYPES = {
  CENTIVO: 'Centivo',
  CENTIVO_EXTERNAL_RBP: 'Centivo + External/RBP',
  EXTERNAL_RBP: 'External/RBP'
}

const SAVE_MODAL_ACTIONS = {
  OPEN_SAVE_NETWORK_MODAL: 'open-save-network-modal',
  OPEN_SAVE_PRE_ENROLLMENT_MODAL: 'open-save-pre-enrollment-modal',
  SAVE_DIRECT: 'save-direct'
}

const NONE_DROPDOWN_CLIENT_OPTION = { title: CLIENT_ID_NONE, plans: null, disabled: false, isSelected: false }
const NONE_SELECTED_CLIENT = { title: CLIENT_ID_NONE, value: '' }
const NONE_SAVE_PAYLOAD = { title: CLIENT_ID_NONE }

const parseJsonDeepClone = (value) => JSON.parse(JSON.stringify(value))

const parseNetworkOptionValues = (networkOptions) =>
  networkOptions?.map((option) => option.value) ?? []

const resolveIsNetworkOptionSelected = (plan, netOpt) =>
  !!(plan.networkAffiliation && plan.networkAffiliation.uuid === netOpt.value)

const resolveIsHrpPlanId = (planId, { match = 'prefix' } = {}) =>
  match === 'prefix'
    ? !!planId?.startsWith('HRP_')
    : !!planId?.includes('HRP_')

const resolvePlanTypeFromPlanId = (planId, { match = 'prefix' } = {}) =>
  resolveIsHrpPlanId(planId, { match })
    ? PLAN_TYPES.HRP
    : PLAN_TYPES.JAVELINA

const isCentivoDirectoryType = (directoryType) =>
  directoryType === DIRECTORY_TYPES.CENTIVO

const isCentivoOrHybridDirectoryType = (directoryType) =>
  directoryType === DIRECTORY_TYPES.CENTIVO ||
  directoryType === DIRECTORY_TYPES.CENTIVO_EXTERNAL_RBP

const parseClientIdentityShape = (clientId, extras = {}) => ({
  title: clientId,
  value: clientId,
  ...extras
})

const parsePlansWithNetworkAffiliation = (plans, { requireUuid = false } = {}) =>
  (plans ?? []).filter(
    (plan) =>
      plan.networkAffiliation &&
      (!requireUuid || plan.networkAffiliation.uuid)
  )

// --- Plan affiliation — parsing & validation ---

/**
 * Checks whether a plan's networkAffiliation uuid appears in the available network options.
 * @param {Object} plan - Plan to check.
 * @param {Array<string>} networkOptionValues - Available network option uuids.
 * @returns {boolean} True when the plan has a known network affiliation.
 */
export const validatePlanHasKnownNetworkAffiliation = (plan, networkOptionValues) => {
  if (!plan.networkAffiliation) return false
  return networkOptionValues.includes(plan.networkAffiliation.uuid)
}

/**
 * Groups a flat list of plans by their clientId.
 * @param {Array<Object>} plans - Plans to group (each must have a clientId field).
 * @returns {Object<string, Array<Object>>} Plans grouped by clientId.
 */
export const parsePlansGroupedByClientId = (plans) =>
  [...plans].reduce(
    (result, item) => ({
      ...result,
      [item.clientId]: [...(result[item.clientId] || []), item]
    }),
    {}
  )

/**
 * Finds the currently selected option in a dropdown options list.
 * @param {Array<Object>} options - Dropdown options, each optionally flagged with isSelected.
 * @returns {Object|undefined} The selected option, if any.
 */
export const resolveSelectedOption = (options) =>
  options?.find((opt) => opt.isSelected)

/**
 * Filters JV and HRP plans down to those that carry a networkAffiliation
 * pointing at one of the currently available network options.
 * @param {Array<Object>} jvPlans - Javelina plans.
 * @param {Array<Object>} hrpPlans - HRP plans.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @returns {Array<Object>} Plans (JV + HRP) that are affiliated to a known network.
 */
export const parseAffiliatedJvAndHrpPlans = (jvPlans, hrpPlans, networkOptions) => {
  const networkOptionValues = parseNetworkOptionValues(networkOptions)
  return [...jvPlans, ...hrpPlans].filter((plan) =>
    validatePlanHasKnownNetworkAffiliation(plan, networkOptionValues)
  )
}

/**
 * Builds networkAffiliationOptions with isSelected flags for a single plan.
 * @param {Object} plan - Plan whose affiliation drives selection.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @returns {Array<Object>} New option objects with isSelected set.
 */
export const parseNetworkAffiliationOptionsForPlan = (plan, networkOptions) =>
  networkOptions.map((netOpt) => ({
    ...netOpt,
    isSelected: resolveIsNetworkOptionSelected(plan, netOpt)
  }))

/**
 * Attaches deep-cloned networkAffiliationOptions to a plan.
 * @param {Object} plan - Plan to annotate.
 * @param {Array<Object>} networkAffiliationOptions - Options to attach.
 * @returns {Object} Plan with networkAffiliationOptions attached.
 */
export const parsePlanWithNetworkAffiliationOptions = (plan, networkAffiliationOptions) => ({
  ...plan,
  networkAffiliationOptions: parseJsonDeepClone(networkAffiliationOptions)
})

/**
 * Sorts plans alphabetically by planId.
 * @param {Array<Object>} plans - Plans to sort.
 * @returns {Array<Object>} Plans sorted by planId.
 */
export const parsePlansSortedByPlanId = (plans) =>
  [...plans].sort((a, b) => a.planId.localeCompare(b.planId))

/**
 * Mutates networkOptions entries in place to reflect the plan's current affiliation selection.
 * @param {Array<Object>} networkOptions - Network options to mutate.
 * @param {Object} plan - Plan whose affiliation drives selection.
 * @returns {Array<Object>} The same networkOptions array with isSelected set on each entry.
 */
export const saveIntoNetworkOptionSelections = (networkOptions, plan) => {
  networkOptions.forEach((netOpt) => {
    netOpt.isSelected = resolveIsNetworkOptionSelected(plan, netOpt)
  })
  return networkOptions
}

/**
 * Attaches networkAffiliationOptions to a single plan based on available network options.
 * @param {Object} plan - Plan to annotate.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {boolean} mutateNetworkOptions - When true, mutates networkOptions entries (solveClientOptionsList side effect).
 * @returns {Object} Plan with networkAffiliationOptions attached.
 */
export const solveAttachNetworkAffiliationOptionsToPlan = (
  plan,
  networkOptions,
  mutateNetworkOptions = false
) => {
  const networkAffiliationOptions = mutateNetworkOptions
    ? saveIntoNetworkOptionSelections(networkOptions, plan)
    : parseNetworkAffiliationOptionsForPlan(plan, networkOptions)

  return parsePlanWithNetworkAffiliationOptions(plan, networkAffiliationOptions)
}

/**
 * Attaches fresh networkAffiliationOptions to each plan while preserving networkAffiliation.
 * @param {Array<Object>} plans - Plans to annotate.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {boolean} mutateNetworkOptions - When true, mutates networkOptions entries in place.
 * @returns {Array<Object>} Plans sorted by planId with networkAffiliationOptions attached.
 */
export const solvePlansWithNetworkAffiliationOptions = (
  plans,
  networkOptions,
  mutateNetworkOptions = false
) =>
  parsePlansSortedByPlanId(
    plans.map((plan) =>
      solveAttachNetworkAffiliationOptionsToPlan(plan, networkOptions, mutateNetworkOptions)
    )
  )

/**
 * Whether a plan's network affiliation belongs to a client other than the one being edited.
 * @param {Object} plan - Plan to check.
 * @param {string} currentClientName - Client name from route params.
 * @returns {boolean} True when affiliation exists and clientName differs from currentClientName.
 */
export const validatePlanAffiliationBelongsToDifferentClient = (plan, currentClientName) =>
  plan.networkAffiliation && plan.networkAffiliation.clientName !== currentClientName

/**
 * Mutates a grouped plan cache entry with networkAffiliationOptions from a resolved plan.
 * @param {Object|undefined} planInGroup - Plan entry in plansGroupedByClientId cache.
 * @param {Object} planWithOptions - Plan carrying networkAffiliationOptions to copy.
 * @returns {void}
 */
export const saveIntoGroupedPlanAffiliationOptions = (planInGroup, planWithOptions) => {
  if (planInGroup) {
    planInGroup.networkAffiliationOptions = planWithOptions.networkAffiliationOptions
  }
}

/**
 * Syncs networkAffiliationOptions into the grouped cache and returns each plan for a client dropdown.
 * @param {string} clientId - Client whose plans to sync.
 * @param {Array<Object>} clientPlans - Plans belonging to the client.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {Object<string, Array<Object>>} plansGroupedByClientId - Plans grouped by clientId (mutated in place).
 * @returns {Array<Object>} Plans with affiliation options synced to the grouped cache.
 */
export const parseClientPlansWithAffiliationOptionsSynced = (
  clientId,
  clientPlans,
  networkOptions,
  plansGroupedByClientId
) =>
  solvePlansWithNetworkAffiliationOptions(clientPlans, networkOptions, true).map((planWithOptions) => {
    const planInGroup = plansGroupedByClientId[clientId].find((p) => p.planId === planWithOptions.planId)
    saveIntoGroupedPlanAffiliationOptions(planInGroup, planWithOptions)
    return planInGroup ?? planWithOptions
  })

/**
 * Builds one client dropdown option with synced plans, disabled flag, and selection state.
 * @param {string} clientId - Client ID for the option.
 * @param {Object<string, Array<Object>>} plansGroupedByClientId - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {string} currentClientName - Client name from route params.
 * @param {string} selectedClientId - Currently selected client ID.
 * @returns {Object} Client dropdown option shape.
 */
export const parseClientDropdownOption = (
  clientId,
  plansGroupedByClientId,
  networkOptions,
  currentClientName,
  selectedClientId
) =>
  parseClientIdentityShape(clientId, {
    plans: parseClientPlansWithAffiliationOptionsSynced(
      clientId,
      plansGroupedByClientId[clientId],
      networkOptions,
      plansGroupedByClientId
    ),
    disabled: plansGroupedByClientId[clientId].some((plan) =>
      validatePlanAffiliationBelongsToDifferentClient(plan, currentClientName)
    ),
    isSelected: selectedClientId === clientId
  })

/**
 * Returns a cloned NONE sentinel for the leading client dropdown entry.
 * @returns {Object} NONE dropdown option with plans null and disabled false.
 */
export const parseNoneClientDropdownOption = () => ({ ...NONE_DROPDOWN_CLIENT_OPTION })

const parseSortedClientOptionsFromGroupedPlans = (plansGroupedByClientId, buildClientOption) => {
  const options = Object.keys(plansGroupedByClientId)
    .map((clientId) => buildClientOption(clientId, plansGroupedByClientId[clientId], plansGroupedByClientId))
    .sort((a, b) => a.title.localeCompare(b.title))
  options.unshift(parseNoneClientDropdownOption())
  return options
}

/**
 * Builds the full client dropdown option list for the plan-affiliation tab.
 * @param {Object<string, Array<Object>>} plansGroupedByClientId - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {string} currentClientName - Client name from route params.
 * @param {string} selectedClientId - Currently selected client ID.
 * @returns {Array<Object>} Sorted client options with a leading NONE entry.
 */
export const solveClientOptionsList = (
  plansGroupedByClientId,
  networkOptions,
  currentClientName,
  selectedClientId
) =>
  parseSortedClientOptionsFromGroupedPlans(plansGroupedByClientId, (clientId) =>
    parseClientDropdownOption(
      clientId,
      plansGroupedByClientId,
      networkOptions,
      currentClientName,
      selectedClientId
    )
  )

/**
 * Whether the configured client snapshot matches the selected plan type.
 * @param {string} planType - The newly selected plan type ("Javelina" or "HRP").
 * @param {Object|null} configuredClient - The DB-backed configured client snapshot.
 * @returns {boolean}
 */
export const validateConfiguredClientMatchesPlanType = (planType, configuredClient) =>
  configuredClient?.planType === planType

/**
 * Maps the clientId after the user switches the plan type radio button.
 * @param {string} planType - The newly selected plan type ("Javelina" or "HRP").
 * @param {Object|null} configuredClient - The DB-backed configured client snapshot.
 * @returns {{clientId: string}} The new clientId for the selected plan type.
 */
export const mapClientIdFromPlanTypeSelection = (planType, configuredClient) => ({
  clientId: validateConfiguredClientMatchesPlanType(planType, configuredClient)
    ? configuredClient.title
    : CLIENT_ID_NONE
})

/**
 * Resolves plans from an explicit array or from the grouped-by-clientId cache.
 * @param {Object} params
 * @param {string} params.clientId - Selected client ID.
 * @param {Array<Object>} [params.plans] - Explicit plans when provided.
 * @param {Object<string, Array<Object>>} [params.plansGroupedByClientId] - Grouped plan cache.
 * @returns {Array<Object>|undefined} Resolved plans for the client.
 */
export const resolvePlansForSelectedClient = ({ clientId, plans, plansGroupedByClientId }) =>
  plans ?? plansGroupedByClientId?.[clientId]

/**
 * Builds a selectedClient shape with no plans attached.
 * @param {string} clientId - Selected client ID.
 * @returns {{ title: string, value: string, plans: undefined }}
 */
export const parseSelectedClientWithoutPlans = (clientId) =>
  parseClientIdentityShape(clientId, { plans: undefined })

/**
 * Builds a selectedClient shape with per-plan networkAffiliationOptions attached.
 * @param {string} clientId - Selected client ID.
 * @param {Array<Object>} resolvedPlans - Plans belonging to the client.
 * @param {Array<{title: string, value: string}>} networkNames - Available network options.
 * @returns {{ title: string, value: string, plans: Array<Object> }}
 */
export const parseSelectedClientWithAffiliationPlans = (clientId, resolvedPlans, networkNames) =>
  parseClientIdentityShape(clientId, {
    plans: solvePlansWithNetworkAffiliationOptions(resolvedPlans, networkNames)
  })

/**
 * Builds selectedClient with per-plan networkAffiliationOptions from explicit plans or grouped cache.
 * @param {Object} params
 * @param {string} params.clientId - Selected client ID.
 * @param {Array<Object>} [params.plans] - Plans belonging to the client; when omitted, resolved from grouped cache.
 * @param {Object<string, Array<Object>>} [params.plansGroupedByClientId] - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} params.networkNames - Available network options.
 * @returns {{ title: string, value: string, plans: Array<Object>|undefined }}
 */
export const solveSelectedClientWithAffiliationOptions = ({
  clientId,
  plans,
  plansGroupedByClientId,
  networkNames
}) => {
  const resolvedPlans = resolvePlansForSelectedClient({ clientId, plans, plansGroupedByClientId })

  if (!resolvedPlans?.length) {
    return parseSelectedClientWithoutPlans(clientId)
  }

  return parseSelectedClientWithAffiliationPlans(clientId, resolvedPlans, networkNames)
}

/**
 * Mutates a plan's networkAffiliationOptions and networkAffiliation when a matching option is selected.
 * @param {Object} plan - Plan to mutate.
 * @param {string} networkUuid - UUID of the newly selected network option.
 * @param {string} currentClientName - Client name from route params.
 * @returns {void}
 */
export const saveIntoPlanAffiliationFromMatchingOption = (plan, networkUuid, currentClientName) => {
  plan.networkAffiliationOptions = plan.networkAffiliationOptions.map((nao) => {
    if (networkUuid === nao.value) {
      nao.isSelected = true
      plan.networkAffiliation = {
        networkName: nao.title,
        uuid: nao.value,
        clientName: currentClientName
      }
    }
    return nao
  })
}

/**
 * Applies a network affiliation selection to the plan matching planId, updating both
 * its networkAffiliationOptions (isSelected flags) and its networkAffiliation field.
 * @param {Array<Object>} plans - The selectedClient's plans.
 * @param {string} planId - The planId whose network affiliation changed.
 * @param {string} networkUuid - The uuid of the newly selected network.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @returns {Array<Object>} The plans with the matching plan's affiliation updated.
 */
export const solveApplyNetworkAffiliationSelection = (plans, planId, networkUuid, currentClientName) =>
  plans.map((p) => {
    if (p.planId === planId) {
      saveIntoPlanAffiliationFromMatchingOption(p, networkUuid, currentClientName)
    }
    return p
  })

/**
 * Applies a network affiliation dropdown change to the selected client snapshot.
 * @param {Object} params
 * @param {Object} params.selectedClient
 * @param {string} params.planId
 * @param {string} params.networkUuid
 * @param {string} params.currentClientName
 * @returns {Object} Updated selectedClient with plans reflecting the new affiliation.
 */
export const solveUserNetworkAffiliationChange = ({
  selectedClient,
  planId,
  networkUuid,
  currentClientName
}) => ({
  ...selectedClient,
  plans: solveApplyNetworkAffiliationSelection(
    selectedClient.plans,
    planId,
    networkUuid,
    currentClientName
  )
})

/**
 * Resolves the client ID from a plan-affiliation client dropdown select payload.
 * @param {{ value?: string, title?: string }} payload - The select event payload.
 * @returns {string|undefined} The resolved client ID.
 */
export const resolveClientIdFromSelectPayload = (payload) =>
  payload?.value || payload?.title

/**
 * Resolves plan ID and network UUID from a plan-affiliation network dropdown select payload.
 * @param {{ name?: string, value?: string }} payload - The select event payload.
 * @returns {{ planId: string|undefined, networkUuid: string|undefined}} The resolved affiliation ids.
 */
export const mapNetworkAffiliationFromSelectPayload = (payload) => ({
  planId: payload?.name,
  networkUuid: payload?.value
})

/**
 * Derives the clientId and planType implied by the first plan in a list of
 * network-affiliated plans.
 * @param {Array<Object>} plans - Plans with a network affiliation.
 * @returns {{clientId: string, planType: string}|null} The derived selection, or null when plans is empty.
 */
export const resolveClientAndPlanTypeFromPlans = (plans) => {
  const firstPlanInAffiliations = plans?.[0]
  if (!firstPlanInAffiliations) return null

  return {
    clientId: firstPlanInAffiliations?.clientId,
    planType: resolvePlanTypeFromPlanId(firstPlanInAffiliations?.planId, { match: 'prefix' })
  }
}

/**
 * Filters plans to a clientId and sorts them alphabetically by planId.
 * @param {string} clientId - Client ID to filter by.
 * @param {Array<Object>} plans - Plans to filter and sort.
 * @returns {Array<Object>} Plans for the client sorted by planId.
 */
export const parsePlansFilteredAndSortedForClient = (clientId, plans) =>
  [...plans]
    .filter((plan) => plan.clientId === clientId)
    .sort((a, b) => a.planId.localeCompare(b.planId))

/**
 * Builds a deep-cloned snapshot of the configured client from saved/backend affiliations.
 * @param {string} clientId - The clientId derived from affiliated plans.
 * @param {string} planType - The plan type ("Javelina" or "HRP").
 * @param {Array<Object>} plans - The network-affiliated plans belonging to that client.
 * @returns {Object} The configuredClient snapshot.
 */
export const parseConfiguredClientSnapshot = (clientId, planType, plans) =>
  parseJsonDeepClone(
    parseClientIdentityShape(clientId, {
      planType,
      plans: parsePlansFilteredAndSortedForClient(clientId, plans),
      disabled: false,
      isSelected: true
    })
  )

/**
 * Builds the payload emitted on the enableSavePlanAffiliation event whenever the
 * selectedClient changes.
 * @param {Object} selectedClient - The client currently selected in the tab.
 * @param {string} planType - The currently selected plan type.
 * @returns {Object} The payload to emit: the selected client plus planType, or a NONE sentinel.
 */
export const resolveEnableSavePlanAffiliationPayload = (selectedClient, planType) => {
  if (selectedClient && selectedClient?.plans?.some((p) => p.networkAffiliation)) {
    return { ...selectedClient, planType }
  }
  return NONE_SAVE_PAYLOAD
}

/**
 * Filters dropdown options by search text (case-insensitive title match).
 * @param {Array<Object>} options - Dropdown options, each with a title.
 * @param {string} searchInputText - The text typed into the dropdown's search input.
 * @returns {Array<Object>} Options whose title contains the search text.
 */
export const parseDropdownOptionsFilteredByText = (options, searchInputText) =>
  options.filter((opt) =>
    opt.title.toLowerCase().includes(searchInputText.toLowerCase())
  )

/**
 * Filters dropdown options by the current search text, when text filtering is active.
 * @param {Array<Object>} options - Dropdown options, each with a title.
 * @param {boolean} filterByText - Whether text filtering is currently active.
 * @param {string} searchInputText - The text typed into the dropdown's search input.
 * @returns {Array<Object>} The filtered (or unfiltered) options.
 */
export const parseFilteredDropdownOptions = (options, filterByText, searchInputText) =>
  filterByText ? parseDropdownOptionsFilteredByText(options, searchInputText) : options

/**
 * Resolves the text to display in a dropdown's search input.
 * @param {boolean} resetSearchText - Whether the search text should be cleared.
 * @param {string} planType - The planType prop, when the dropdown is used as a plan-type-scoped selector.
 * @param {string} name - The dropdown's name prop.
 * @param {Array<Object>} options - Dropdown options, each optionally flagged with isSelected.
 * @returns {string|undefined} The text to display in the search input.
 */
export const resolveSearchInputText = (resetSearchText, planType, name, options) => {
  if (resetSearchText) return ''
  if (planType) return name
  return resolveSelectedOption(options)?.title
}

/**
 * Builds the network name dropdown options from a client's network configs.
 * @param {Array<Object>} networkConfigs - The client's draft network configs.
 * @returns {Array<{title: string, value: string}>} The network name options.
 */
export const parseNetworkNameOptions = (networkConfigs) =>
  networkConfigs
    ?.filter((nc) => nc && nc.name)
    .map((nc) => ({ title: nc.name, value: nc.uuid })) ?? []

/**
 * Resolves HRP vs Javelina from the first affiliated plan in a client's plan list.
 * @param {Array<Object>} clientPlans - Plans belonging to one client.
 * @param {Array<{title: string, value: string}>} networkNames - Available network name options.
 * @returns {string} PLAN_TYPES.HRP or PLAN_TYPES.JAVELINA.
 */
export const resolvePlanTypeFromClientPlans = (clientPlans, networkNames) => {
  const networkOptionValues = parseNetworkOptionValues(networkNames)
  const oneOfTheSelectedPlan = clientPlans.find((plan) =>
    validatePlanHasKnownNetworkAffiliation(plan, networkOptionValues)
  )
  return resolvePlanTypeFromPlanId(oneOfTheSelectedPlan?.planId, { match: 'contains' })
}

/**
 * Builds a client summary option for the parent client-affiliations dropdown.
 * @param {string} clientId - Client ID for the summary.
 * @param {Object<string, Array<Object>>} groupByClientId - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} networkNames - Available network name options.
 * @param {string} currentClientName - Client name from route params.
 * @returns {Object} Client summary with planType, disabled, and isSelected flags.
 */
export const parseClientSummaryFromGroupedPlans = (
  clientId,
  groupByClientId,
  networkNames,
  currentClientName
) => {
  const clientPlans = groupByClientId[clientId]
  const networkOptionValues = parseNetworkOptionValues(networkNames)

  return {
    title: clientId,
    plans: clientPlans,
    planType: resolvePlanTypeFromClientPlans(clientPlans, networkNames),
    disabled: clientPlans.some((plan) =>
      validatePlanAffiliationBelongsToDifferentClient(plan, currentClientName)
    ),
    isSelected: clientPlans.some((plan) =>
      validatePlanHasKnownNetworkAffiliation(plan, networkOptionValues)
    )
  }
}

/**
 * Builds the "client" dropdown option list (used in the parent client-affiliations summary)
 * from a flat list of plans and the currently available network names.
 * @param {Array<Object>} plans - All plans (JV + HRP) for the client's plan types.
 * @param {Array<{title: string, value: string}>} networkNames - Available network name options.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @returns {Array<Object>} Client dropdown options, including a leading "NONE" entry.
 */
export const solveClientsFromPlans = (plans = [], networkNames, currentClientName) => {
  const groupByClientId = parsePlansGroupedByClientId(plans)

  return parseSortedClientOptionsFromGroupedPlans(groupByClientId, (clientId) =>
    parseClientSummaryFromGroupedPlans(clientId, groupByClientId, networkNames, currentClientName)
  )
}

/**
 * Extracts the network affiliation uuids present in a list of plans.
 * @param {Array<Object>} plans - Plans to scan.
 * @returns {Array<string>} The uuids of each plan's networkAffiliation, when present.
 */
export const parseNetworkAffiliationUuids = (plans) =>
  parsePlansWithNetworkAffiliation(plans, { requireUuid: true }).map(
    (plan) => plan.networkAffiliation.uuid
  )

/**
 * Maps static fields for the updatePlanNetworkAffiliationForClient request body.
 * @param {Object|undefined} planAffiliations - Draft planAffiliations value.
 * @param {string} clientName - Client display name.
 * @returns {{ clientId: string|undefined, clientName: string, planType: string|undefined, affiliations: Array<Object>, shouldGetHrpPlans: boolean }}
 */
export const mapAffiliationRequestStaticFields = (planAffiliations, clientName) => ({
  clientId: planAffiliations?.title,
  clientName,
  planType: planAffiliations?.planType,
  affiliations: [],
  shouldGetHrpPlans: true
})

/**
 * Maps planAffiliations plans to affiliation payload entries, or null for NONE / missing plans.
 * @param {Object|undefined} planAffiliations - Draft planAffiliations value.
 * @returns {Array<{ planId: string, uuid: string, networkName: string }>|null}
 */
export const parseAffiliationsFromPlanAffiliations = (planAffiliations) => {
  if (planAffiliations?.title === CLIENT_ID_NONE || !planAffiliations?.plans) {
    return null
  }

  return parsePlansWithNetworkAffiliation(planAffiliations.plans).map((plan) => ({
    planId: plan.planId,
    uuid: plan.networkAffiliation.uuid,
    networkName: plan.networkAffiliation.networkName
  }))
}

/**
 * Builds the request body for the updatePlanNetworkAffiliationForClient API call.
 * @param {Object} planAffiliations - The draft's planAffiliations value (a selectedClient-shaped object).
 * @param {string} clientName - The client's display name.
 * @returns {{clientId: string, clientName: string, planType: string, affiliations: Array<Object>|null, shouldGetHrpPlans: boolean}}
 *   The request body to send to updatePlanNetworkAffiliationForClient.
 */
export const solveAffiliationRequestBody = (planAffiliations, clientName) => ({
  ...mapAffiliationRequestStaticFields(planAffiliations, clientName),
  affiliations: parseAffiliationsFromPlanAffiliations(planAffiliations)
})

/**
 * Splits a list of plans into Javelina and HRP buckets based on the HRP_ planId prefix.
 * @param {Array<Object>} plans - Plans to partition.
 * @returns {{jvPlans: Array<Object>, hrpPlans: Array<Object>}} The partitioned plans.
 */
export const parsePlansPartitionedByNamespace = (plans) => ({
  jvPlans: plans.filter((p) => !resolveIsHrpPlanId(p.planId, { match: 'contains' })),
  hrpPlans: plans.filter((p) => resolveIsHrpPlanId(p.planId, { match: 'contains' }))
})

/**
 * Mutates a plan's networkAffiliation from a save-response updated plan entry.
 * @param {Object} plan - Plan to mutate in the store cache.
 * @param {Object|undefined} updatedPlan - Updated plan from the save response.
 * @returns {void}
 */
export const saveIntoPlanNetworkAffiliationFromUpdate = (plan, updatedPlan) => {
  plan.networkAffiliation = updatedPlan?.networkAffiliation
}

/**
 * Merges freshly saved network affiliations into the existing store plans.
 * Each plan is matched by planId; networkAffiliation may be null when an affiliation was removed.
 *
 * @param {Array<Object>} existingPlans - The full existing plans list from the store (jvPlans or hrpPlans).
 * @param {Array<Object>} updatedPlans - The subset of plans whose affiliation changed, from the save response.
 * @returns {Array<Object>} existingPlans with matching entries' networkAffiliation replaced.
 */
export const solveNetworkAffiliationUpdates = (existingPlans, updatedPlans) => {
  const planIdsToUpdate = updatedPlans.map((p) => p.planId)
  return existingPlans.map((p) => {
    if (planIdsToUpdate.includes(p.planId)) {
      const updatedPlan = updatedPlans.find((up) => up.planId === p.planId)
      saveIntoPlanNetworkAffiliationFromUpdate(p, updatedPlan)
    }
    return p
  })
}

// --- Plan affiliation — orchestration & UI state ---

/**
 * Groups JV and HRP plans by clientId for plan-affiliation dropdown caches.
 * @param {Array<Object>} jvPlans - Javelina plans from the store.
 * @param {Array<Object>} hrpPlans - HRP plans from the store.
 * @returns {{ jvPlansGroupedByClientId: Object, hrpPlansGroupedByClientId: Object }}
 */
export const parseJvAndHrpPlansGroupedByClientId = (jvPlans, hrpPlans) => ({
  jvPlansGroupedByClientId: parsePlansGroupedByClientId(jvPlans),
  hrpPlansGroupedByClientId: parsePlansGroupedByClientId(hrpPlans)
})

/**
 * Derives the saved configured-client snapshot from store plans and available networks.
 * @param {Object} params
 * @param {Array<Object>} params.jvPlans - Javelina plans from the store.
 * @param {Array<Object>} params.hrpPlans - HRP plans from the store.
 * @param {Array<{title: string, value: string}>} params.networkNames - Available network options.
 * @returns {Object|null} configuredClient snapshot or null when no affiliations match.
 */
export const solveConfiguredClientFromStore = ({ jvPlans, hrpPlans, networkNames }) => {
  const affiliatedPlans = parseAffiliatedJvAndHrpPlans(jvPlans, hrpPlans, networkNames)
  const derivedSelection = resolveClientAndPlanTypeFromPlans(affiliatedPlans)

  if (!derivedSelection) {
    return null
  }

  const clientPlans = affiliatedPlans.filter(
    (plan) => plan.clientId === derivedSelection.clientId
  )

  return parseConfiguredClientSnapshot(
    derivedSelection.clientId,
    derivedSelection.planType,
    clientPlans
  )
}

const resolvePlansGroupedByClientIdForPlanType = (planType, jvPlansGroupedByClientId, hrpPlansGroupedByClientId) =>
  planType === PLAN_TYPES.HRP ? hrpPlansGroupedByClientId : jvPlansGroupedByClientId

/**
 * Whether plan affiliation selection should use configuredClient plans snapshot.
 * @param {Object} params
 * @param {string} params.clientId
 * @param {string} params.planType
 * @param {Object|null} [params.configuredClient]
 * @returns {boolean}
 */
export const validateUseConfiguredPlansForAffiliationSelection = ({ clientId, planType, configuredClient }) =>
  !!(
    configuredClient &&
    clientId === configuredClient.title &&
    planType === configuredClient.planType
  )

const solveSelectedClientForPlanAffiliationState = ({
  clientId,
  planType,
  configuredClient,
  plansGroupedByClientId,
  networkNames
}) => {
  if (!clientId || clientId === CLIENT_ID_NONE) {
    return NONE_SELECTED_CLIENT
  }

  const useConfiguredPlans = validateUseConfiguredPlansForAffiliationSelection({
    clientId,
    planType,
    configuredClient
  })

  return solveSelectedClientWithAffiliationOptions({
    clientId,
    plans: useConfiguredPlans
      ? parseJsonDeepClone(configuredClient.plans)
      : undefined,
    plansGroupedByClientId,
    networkNames
  })
}

/**
 * Returns an empty plan-affiliation UI state.
 * @returns {Object} Empty planAffiliation state plus empty grouped caches.
 */
export const parseEmptyPlanAffiliationState = () => ({
  planType: '',
  clientId: '',
  configuredClient: null,
  selectedClient: NONE_SELECTED_CLIENT,
  clientOptions: [],
  jvPlansGroupedByClientId: {},
  hrpPlansGroupedByClientId: {}
})

/**
 * Maps planType and clientId from an explicit selection payload.
 * @param {{ planType: string, clientId: string }} selection
 * @returns {{ planType: string, clientId: string }}
 */
export const mapPlanAffiliationSelectionFromSelection = (selection) => ({
  planType: selection.planType,
  clientId: selection.clientId
})

/**
 * Maps planType and clientId from a configured client snapshot.
 * @param {Object} configuredClient
 * @returns {{ planType: string, clientId: string }}
 */
export const mapPlanAffiliationSelectionFromConfiguredClient = (configuredClient) => ({
  planType: configuredClient.planType,
  clientId: configuredClient.title
})

/**
 * Returns an empty plan-affiliation selection shape.
 * @returns {{ planType: string, clientId: string }}
 */
export const parseEmptyPlanAffiliationSelection = () => ({
  planType: '',
  clientId: ''
})

/**
 * Resolves planType and clientId from explicit selection or configuredClient fallback.
 * @param {Object} params
 * @param {{ planType: string, clientId: string }|null} [params.selection]
 * @param {Object|null} [params.configuredClient]
 * @returns {{ planType: string, clientId: string }}
 */
export const resolvePlanAffiliationSelection = ({ selection, configuredClient }) => {
  if (selection) {
    return mapPlanAffiliationSelectionFromSelection(selection)
  }
  if (configuredClient) {
    return mapPlanAffiliationSelectionFromConfiguredClient(configuredClient)
  }
  return parseEmptyPlanAffiliationSelection()
}

/**
 * Orchestrates the full planAffiliation UI state from store plans and network options.
 * @param {Object} params
 * @param {Array<Object>} [params.jvPlans] - Javelina plans from the store.
 * @param {Array<Object>} [params.hrpPlans] - HRP plans from the store.
 * @param {Array<{title: string, value: string}>} [params.networkNames] - Available network options.
 * @param {string} params.currentClientName - Route client name being edited.
 * @param {{ planType: string, clientId: string }|null} [params.selection] - When set, preserves user selection.
 * @param {boolean} [params.reset] - When true, returns empty state.
 * @returns {Object} Full planAffiliation state plus grouped plan caches.
 */
export const solvePlanAffiliationState = ({
  jvPlans = [],
  hrpPlans = [],
  networkNames = [],
  currentClientName,
  selection = null,
  reset = false
}) => {
  if (reset) {
    return parseEmptyPlanAffiliationState()
  }

  const { jvPlansGroupedByClientId, hrpPlansGroupedByClientId } = parseJvAndHrpPlansGroupedByClientId(
    jvPlans,
    hrpPlans
  )
  const configuredClient = solveConfiguredClientFromStore({ jvPlans, hrpPlans, networkNames })
  const { planType, clientId } = resolvePlanAffiliationSelection({ selection, configuredClient })

  const plansGroupedByClientId = resolvePlansGroupedByClientIdForPlanType(
    planType,
    jvPlansGroupedByClientId,
    hrpPlansGroupedByClientId
  )

  const selectedClient = solveSelectedClientForPlanAffiliationState({
    clientId,
    planType,
    configuredClient,
    plansGroupedByClientId,
    networkNames
  })

  const clientOptions = solveClientOptionsList(
    plansGroupedByClientId,
    networkNames,
    currentClientName,
    clientId
  )

  return {
    planType,
    clientId,
    configuredClient,
    selectedClient,
    clientOptions,
    jvPlansGroupedByClientId,
    hrpPlansGroupedByClientId
  }
}

// --- Networks tab ---

const NETWORKS_CLIENT_NAME_EMPTY_ERROR = 'Please fill out this form'
const NETWORKS_CLIENT_NAME_DUPLICATE_ERROR = 'Choose a different client ID this one is not available'
const NETWORKS_CLIENT_NAME_INVALID_CHARS_ERROR = 'Symbols are not allowed in the client name'
const NETWORKS_CLIENT_NAME_ALLOWED_CHARS_REGEX = /^[A-Za-z0-9\s]*$/

/**
 * Resolves client name validation error for the Networks tab.
 * @param {Object} params
 * @param {string} params.name - Raw client name from draft.
 * @param {string|undefined} params.initialName - Original client name when editing (prop from parent).
 * @param {string[]} params.clientNameList - Lowercase client names from store getter.
 * @returns {string|undefined} Error message when invalid; undefined when valid.
 */
export const resolveNetworksClientNameError = ({ name, initialName, clientNameList }) => {
  const lowerName = name.toLowerCase().trim()

  if (!lowerName.length) return NETWORKS_CLIENT_NAME_EMPTY_ERROR
  if (
    clientNameList?.length &&
    lowerName !== initialName &&
    clientNameList.includes(lowerName)
  ) {
    return NETWORKS_CLIENT_NAME_DUPLICATE_ERROR
  }
  if (!NETWORKS_CLIENT_NAME_ALLOWED_CHARS_REGEX.test(lowerName)) {
    return NETWORKS_CLIENT_NAME_INVALID_CHARS_ERROR
  }
  return undefined
}

/**
 * Whether the Networks form should signal save enabled to the parent.
 * @param {Object} params
 * @param {string} params.name - Draft client name.
 * @param {string|undefined} params.clientNameError - Current validation error, if any.
 * @param {Array<Object>|undefined} params.networkConfigs - Draft network config rows.
 * @returns {boolean}
 */
export const resolveNetworksFormSaveEnabled = ({ name, clientNameError, networkConfigs }) =>
  !!name &&
  !!name.trim() &&
  !clientNameError &&
  !!networkConfigs &&
  networkConfigs.length > 0

/**
 * Returns networkConfigs with one new row appended (immutable).
 * @param {Array<Object>} networkConfigs - Current rows.
 * @param {Object} newRow - Row from addClientConfig() (caller supplies UUID).
 * @returns {Array<Object>}
 */
export const parseNetworkConfigsWithAppendedRow = (networkConfigs, newRow) =>
  [...networkConfigs, newRow]

/**
 * Removes a network row; ensures at least one row remains by appending emptyRow when needed.
 * @param {Object} params
 * @param {Array<Object>} params.networkConfigs - Current rows.
 * @param {number} params.index - Row index to remove.
 * @param {Object} params.emptyRow - Row from addClientConfig() when re-adding after last delete.
 * @returns {{ networkConfigs: Array<Object>, lastIndex: number }}
 */
export const parseNetworkConfigsAfterRowDelete = ({ networkConfigs, index, emptyRow }) => {
  const withoutDeleted = networkConfigs.filter((_, i) => i !== index)
  const nextConfigs =
    withoutDeleted.length < 1
      ? parseNetworkConfigsWithAppendedRow(withoutDeleted, emptyRow)
      : withoutDeleted

  return {
    networkConfigs: nextConfigs,
    lastIndex: nextConfigs.length - 1
  }
}

/**
 * Parses even-index option indices for network series dropdown layout.
 * @param {number} seriesCount - Object.keys(networkSeriesOptions).length
 * @returns {number[]}
 */
export const parseEvenOptionIndicesFromSeriesCount = (seriesCount) => {
  const evenOptions = []
  for (let i = 0; i < seriesCount; i += 2) {
    evenOptions.push(i)
  }
  return evenOptions
}

// --- Pre-enrollment tab ---

const PRE_ENROLLMENT_URL_ALLOWED_CHARS_REGEX = /^[A-Za-z0-9.-]*$/
const PRE_ENROLLMENT_URL_VALID_SUFFIXES = ['.centivo.com', '.centivo.dev', '.centivo.live']
const PRE_ENROLLMENT_URL_INVALID_ERROR = 'URL is invalid'

/**
 * Formats the TLD suffix from the API URL for pre-enrollment domain display hints.
 * @param {string|undefined} [apiUrl] - Defaults to process.env.VUE_APP_API_URL when omitted.
 * @returns {string} TLD suffix (e.g. 'com', 'live').
 */
export const formatDomainSuffixFromEnv = (apiUrl = process.env.VUE_APP_API_URL) =>
  apiUrl?.split('.').pop() || 'com'

/**
 * Resolves pre-enrollment site URL format error (characters and centivo domain suffix).
 * @param {string} url - Raw site URL from draft input.
 * @returns {string|undefined} Error message when invalid; undefined when valid.
 */
export const resolvePreEnrollmentUrlFormatError = (url) => {
  if (
    !PRE_ENROLLMENT_URL_ALLOWED_CHARS_REGEX.test(url) ||
    !PRE_ENROLLMENT_URL_VALID_SUFFIXES.some((suffix) => url.endsWith(suffix))
  ) {
    return PRE_ENROLLMENT_URL_INVALID_ERROR
  }
  return undefined
}

/**
 * Maps URL format or API uniqueness/validity result to parent pre-enrollment invalid flag.
 * @param {Object} params
 * @param {string|null|undefined} [params.urlFormatError] - From resolvePreEnrollmentUrlFormatError.
 * @param {boolean|null|undefined} [params.unique] - From checkOeURL API (caller supplies).
 * @param {boolean|null|undefined} [params.valid] - From checkOeURL API.
 * @returns {boolean} true when parent should mark pre-enrollment invalid.
 */
export const resolvePreEnrollmentInvalidFromUrlCheck = ({ urlFormatError, unique, valid } = {}) => {
  if (urlFormatError) return true
  const uniqueIsUnset = unique === null || unique === undefined
  const validIsUnset = valid === null || valid === undefined
  if (uniqueIsUnset || validIsUnset) {
    return false
  }
  return !unique || !valid
}

/**
 * Whether the Publish/Unpublish button should be disabled on the Pre-enrollment tab.
 * @param {Object} params
 * @param {string} params.siteURL
 * @param {boolean|undefined} params.customContent
 * @param {boolean|null|undefined} params.isUrlUnique
 * @param {boolean} params.isUrlValid
 * @param {string|null|undefined} params.urlError
 * @returns {boolean}
 */
export const resolveIsPublishDisabled = ({
  siteURL,
  customContent,
  isUrlUnique,
  isUrlValid,
  urlError
}) =>
  !siteURL ||
  customContent !== false ||
  isUrlUnique === false ||
  isUrlValid === false ||
  !!urlError

/**
 * Whether toggling custom content off should open the confirm-removal modal.
 * @param {Object} params
 * @param {boolean} params.canDeleteCustomContent - User already confirmed removal.
 * @param {boolean|undefined} params.persistedCustomContent - clientData.preEnrollment.customContent.
 * @returns {boolean}
 */
export const resolveConfirmCustomContentRemoval = ({
  canDeleteCustomContent,
  persistedCustomContent
}) => !canDeleteCustomContent && persistedCustomContent === true

/**
 * Whether checkURL should return early without calling the uniqueness API.
 * @param {Object} params
 * @param {string} params.eventType - DOM event type ('blur' | 'keydown').
 * @param {string} [params.key] - KeyboardEvent.key when eventType is 'keydown'.
 * @param {boolean|null|undefined} params.isUrlUnique - Current uniqueness state.
 * @returns {boolean}
 */
export const validateSkipUrlCheckOnEvent = ({ eventType, key, isUrlUnique }) => {
  if (eventType === 'blur' && isUrlUnique !== null) return true
  if (eventType === 'keydown' && key !== 'Enter') return true
  return false
}

// --- Save normalization ---

const parseCentivoNetworkConfigRowForSave = (normalized) =>
  _.omit(normalized, ['rbpAdministrator', 'thirdPartyNetwork', 'requireVpcModule'])

const parseCentivoExternalRbpNetworkConfigRowForSave = (normalized) =>
  _.omit(normalized, ['requireVpcModule'])

const parseExternalRbpNetworkConfigRowForSave = (normalized) => ({
  ..._.omit(normalized, ['preferredNetworkCodes']),
  networkSeries: []
})

const parsePreferredNetworkCodesForSave = (normalized) => ({
  ...normalized,
  preferredNetworkCodes: Array.isArray(normalized.preferredNetworkCodes)
    ? [...normalized.preferredNetworkCodes]
    : []
})

const parseNetworkConfigRowForSave = (config) => {
  let normalized = _.cloneDeep(config)

  if (normalized.name) {
    normalized.name = normalized.name.trim()
  }

  if (normalized && isCentivoDirectoryType(normalized.directoryType)) {
    normalized = parseCentivoNetworkConfigRowForSave(normalized)
  } else if (
    normalized &&
    normalized.directoryType === DIRECTORY_TYPES.CENTIVO_EXTERNAL_RBP
  ) {
    normalized = parseCentivoExternalRbpNetworkConfigRowForSave(normalized)
  } else if (normalized && normalized.directoryType === DIRECTORY_TYPES.EXTERNAL_RBP) {
    normalized = parseExternalRbpNetworkConfigRowForSave(normalized)
  }

  if (normalized && isCentivoOrHybridDirectoryType(normalized.directoryType)) {
    normalized = parsePreferredNetworkCodesForSave(normalized)
  }

  return _.omit(normalized, ['isValid'])
}

/**
 * Returns whether a single network config row is valid for save.
 * @param {object} config
 * @returns {boolean}
 */
export const validateNetworkConfigRow = (config) => config.isValid !== false

const parseNetworkConfigsSaveFailureResult = (normalizedPrefix, networkConfigs, index) => ({
  networksInvalid: true,
  errorIndex: 0,
  networkConfigs: [...normalizedPrefix, ...networkConfigs.slice(index)]
})

/**
 * Validates and parses network config rows in a single forward pass.
 * @param {Array<object>} networkConfigs
 * @returns {{ networksInvalid: boolean, errorIndex: number, networkConfigs: Array<object> }}
 */
export const solveNetworkConfigsForSave = (networkConfigs) => {
  const normalizedPrefix = []

  for (let index = 0; index < networkConfigs.length; index++) {
    const config = networkConfigs[index]

    if (!validateNetworkConfigRow(config)) {
      return parseNetworkConfigsSaveFailureResult(normalizedPrefix, networkConfigs, index)
    }

    normalizedPrefix.push(parseNetworkConfigRowForSave(config))
  }

  return {
    networksInvalid: false,
    errorIndex: 0,
    networkConfigs: normalizedPrefix
  }
}

/**
 * Syncs one plan's affiliation networkName and option titles from network config map.
 * @param {object} plan
 * @param {Map<string, object>} networkConfigByUuid
 * @returns {object}
 */
export const parsePlanAffiliationPlanNetworkNamesFromConfig = (plan, networkConfigByUuid) => {
  const normalizedPlan = _.cloneDeep(plan)
  const config = networkConfigByUuid.get(normalizedPlan.networkAffiliation.uuid)

  if (config && normalizedPlan.networkAffiliation.networkName !== config.name) {
    normalizedPlan.networkAffiliation.networkName = config.name
  }

  normalizedPlan.networkAffiliationOptions = (normalizedPlan.networkAffiliationOptions ?? []).map(
    (option) => {
      const optionConfig = networkConfigByUuid.get(option.value)

      if (optionConfig && option.title !== optionConfig.name) {
        return { ...option, title: optionConfig.name }
      }

      return option
    }
  )

  return normalizedPlan
}

/**
 * Parses plan affiliation networkName and option titles from network configs.
 * @param {{ plans?: Array<object> }} [planAffiliations]
 * @param {Array<{ uuid: string, name: string }>} networkConfigs
 * @returns {{ plans?: Array<object> }|undefined}
 */
export const parsePlanAffiliationNetworkNamesFromConfigs = (planAffiliations, networkConfigs) => {
  if (!planAffiliations?.plans) {
    return planAffiliations
  }

  const networkConfigByUuid = new Map(networkConfigs.map((config) => [config.uuid, config]))

  return {
    ...planAffiliations,
    plans: planAffiliations.plans.map((plan) =>
      parsePlanAffiliationPlanNetworkNamesFromConfig(plan, networkConfigByUuid)
    )
  }
}

/**
 * Whether the Pre-enrollment tab should be enabled.
 * @param {Array<{ includePreEnrollmentSite?: boolean }>} networkConfigs
 * @returns {boolean}
 */
export const resolvePreEnrollmentTabEnabled = (networkConfigs) =>
  !!networkConfigs?.find((config) => config && config.includePreEnrollmentSite)

/**
 * Whether the Plan affiliation tab should be enabled.
 * @param {object|undefined} clientData
 * @returns {boolean}
 */
export const resolvePlanAffiliationTabEnabled = (clientData) => !!clientData?.name

/**
 * @typedef {Object} ClientConfigClientData
 * @property {string} clientId - Employer client id (often derived from planUuid before `#`).
 * @property {string} name - Display name for the client config.
 * @property {object|undefined} planAffiliations - Persisted plan affiliation snapshot.
 * @property {Array<object>} networkConfigs - Network rows for this client.
 * @property {{ siteURL: string, customContent: boolean, publish: boolean, hasCustomization: boolean }} preEnrollment
 * @property {boolean} hasPcpNetwork
 */

const parseEmptyPreEnrollmentClientData = () => ({
  siteURL: '',
  customContent: false,
  publish: false,
  hasCustomization: false
})

/**
 * Derives clientId from a planUuid (`LSINC#HRP_LSINCEPO` → `LSINC`).
 * @param {string|undefined} planUuid
 * @returns {string}
 */
export const parseClientIdFromPlanUuid = (planUuid) => {
  if (!planUuid || !planUuid.includes('#')) {
    return ''
  }

  return planUuid.split('#')[0]
}

/**
 * Returns the empty ClientConfig persisted clientData skeleton.
 * @returns {ClientConfigClientData}
 */
export const parseEmptyClientData = () => ({
  clientId: '',
  name: '',
  planAffiliations: undefined,
  networkConfigs: [],
  preEnrollment: parseEmptyPreEnrollmentClientData(),
  hasPcpNetwork: false
})

/**
 * Resolves clientId from plan affiliation plans when the API omits or mislabels it.
 * @param {object|undefined} planAffiliations
 * @returns {string}
 */
const resolveClientIdFromPlanAffiliations = (planAffiliations) => {
  const firstPlan = planAffiliations?.plans?.[0]
  if (!firstPlan) {
    return planAffiliations?.value ?? ''
  }

  return (
    parseClientIdFromPlanUuid(firstPlan.planUuid) ||
    firstPlan.clientId ||
    planAffiliations?.value ||
    ''
  )
}

/**
 * Maps a GET/save API response into the ClientConfig clientData shape.
 * @param {object|undefined} data
 * @returns {ClientConfigClientData}
 */
export const parseClientDataFromResponse = (data) => {
  const emptyClientData = parseEmptyClientData()

  if (!data) {
    return emptyClientData
  }

  const planAffiliations = data.planAffiliations ?? emptyClientData.planAffiliations
  const preEnrollment = data.preEnrollment
    ? {
      siteURL: data.preEnrollment.siteURL ?? '',
      customContent: data.preEnrollment.customContent ?? false,
      publish: data.preEnrollment.publish ?? false,
      hasCustomization: data.preEnrollment.hasCustomization ?? false
    }
    : emptyClientData.preEnrollment

  return {
    clientId: resolveClientIdFromPlanAffiliations(planAffiliations),
    name: data.name ?? '',
    planAffiliations,
    networkConfigs: data.networkConfigs ?? [],
    preEnrollment,
    hasPcpNetwork: data.hasPcpNetwork ?? false
  }
}

const parseComparableNetworkConfigRow = (config) => {
  if (!config) {
    return config
  }

  const { isValid, ...comparableRow } = config
  return comparableRow
}

const parseComparablePlanAffiliationPlan = (plan) => {
  if (!plan) {
    return plan
  }

  const { networkAffiliationOptions, ...comparablePlan } = plan
  return comparablePlan
}

const parseComparablePlanAffiliations = (planAffiliations) => {
  if (!planAffiliations) {
    return undefined
  }

  const { isSelected, disabled, ...rest } = planAffiliations

  return {
    ...rest,
    plans: (rest.plans ?? []).map(parseComparablePlanAffiliationPlan)
  }
}

/**
 * Builds a UI-field-free plan affiliation snapshot for Save enablement comparisons.
 * @param {object|undefined} planAffiliations
 * @returns {{ clientId: string, planType: string, affiliations: Array<{ planId: string, uuid: string }>|null }}
 */
export const parseComparablePlanAffiliationSaveState = (planAffiliations) => {
  if (!planAffiliations || planAffiliations.title === CLIENT_ID_NONE) {
    return {
      clientId: CLIENT_ID_NONE,
      planType: '',
      affiliations: null
    }
  }

  const affiliations = parseAffiliationsFromPlanAffiliations(planAffiliations)
  const comparableAffiliations = affiliations
    ? affiliations
      .map(({ planId, uuid }) => ({ planId, uuid }))
      .sort((left, right) => left.planId.localeCompare(right.planId))
    : null

  return {
    clientId: planAffiliations.title,
    planType: planAffiliations.planType ?? '',
    affiliations: comparableAffiliations
  }
}

const parseComparablePreEnrollment = (preEnrollment) => {
  if (!preEnrollment) {
    return undefined
  }

  return {
    siteURL: preEnrollment.siteURL ?? '',
    customContent: preEnrollment.customContent ?? false,
    publish: preEnrollment.publish ?? false,
    hasCustomization: preEnrollment.hasCustomization ?? false
  }
}

/**
 * Builds a normalized client-config snapshot for dirty-check comparisons.
 * @param {Object} params
 * @param {string} [params.name]
 * @param {Array<object>} [params.networkConfigs]
 * @param {object} [params.preEnrollment]
 * @param {object} [params.planAffiliations]
 * @returns {object}
 */
export const parseComparableClientConfig = ({
  name,
  networkConfigs,
  preEnrollment,
  planAffiliations
}) => {
  const comparable = {
    name: name?.trim?.() ?? name ?? '',
    networkConfigs: (networkConfigs ?? []).map(parseComparableNetworkConfigRow)
  }

  const comparablePreEnrollment = parseComparablePreEnrollment(preEnrollment)
  if (comparablePreEnrollment) {
    comparable.preEnrollment = comparablePreEnrollment
  }

  const comparablePlanAffiliations = parseComparablePlanAffiliations(planAffiliations)
  if (comparablePlanAffiliations) {
    comparable.planAffiliations = comparablePlanAffiliations
  }

  return comparable
}

/**
 * Maps persisted clientData into the comparable snapshot shape.
 * @param {object|undefined} clientData
 * @returns {object}
 */
export const parseComparableFromClientData = (clientData) =>
  parseComparableClientConfig({
    name: clientData?.name,
    networkConfigs: clientData?.networkConfigs,
    preEnrollment: clientData?.preEnrollment,
    planAffiliations: clientData?.planAffiliations
  })

/**
 * Whether tab-owned domain state differs from persisted clientData.
 * @param {object} currentComparable
 * @param {object|undefined} persistedComparable
 * @returns {boolean}
 */
export const resolveHasUnsavedChanges = (currentComparable, persistedComparable) =>
  !_.isEqual(currentComparable, persistedComparable ?? {})

/**
 * Assembles the client config API payload from tab domain fields without mutating inputs.
 * @param {Object} params
 * @param {string} params.name
 * @param {Array<object>} params.networkConfigs
 * @param {object|undefined} params.preEnrollment
 * @param {object|undefined} params.planAffiliations
 * @returns {object}
 */
export const parseClientConfigPayloadFromTabStates = ({
  name,
  networkConfigs,
  preEnrollment,
  planAffiliations
}) => {
  const payload = {
    name: name?.trim?.() ?? name ?? '',
    networkConfigs: networkConfigs ?? []
  }

  const comparablePreEnrollment = parseComparablePreEnrollment(preEnrollment)
  if (comparablePreEnrollment) {
    payload.preEnrollment = comparablePreEnrollment
  }

  if (planAffiliations) {
    payload.planAffiliations = parseJsonDeepClone(planAffiliations)
  }

  return payload
}

/**
 * Validates, normalizes, and assembles the client config save payload from tab domain state.
 * @param {Object} params
 * @param {string} params.name
 * @param {Array<object>} params.networkConfigs
 * @param {object|undefined} params.preEnrollment
 * @param {object|undefined} params.planAffiliations
 * @returns {{ valid: boolean, payload?: object, networksInvalid?: boolean, errorIndex?: number, networkConfigs?: Array<object> }}
 */
export const solveClientConfigSavePayload = ({
  name,
  networkConfigs,
  preEnrollment,
  planAffiliations
}) => {
  const saveResult = solveNetworkConfigsForSave(networkConfigs ?? [])

  if (saveResult.networksInvalid) {
    return {
      valid: false,
      networksInvalid: true,
      errorIndex: saveResult.errorIndex,
      networkConfigs: saveResult.networkConfigs
    }
  }

  const normalizedPlanAffiliations = parsePlanAffiliationNetworkNamesFromConfigs(
    planAffiliations,
    saveResult.networkConfigs
  )

  return {
    valid: true,
    payload: parseClientConfigPayloadFromTabStates({
      name,
      networkConfigs: saveResult.networkConfigs,
      preEnrollment,
      planAffiliations: normalizedPlanAffiliations
    })
  }
}

/**
 * Whether Pre-enrollment tab save should be enabled.
 * @param {Object} params
 * @param {boolean|undefined} params.isPublished
 * @param {boolean} params.preEnrollmentInvalid
 * @param {boolean} params.hasUnsavedChanges
 * @returns {boolean}
 */
export const validatePreEnrollmentSaveEnabled = ({
  isPublished,
  preEnrollmentInvalid,
  hasUnsavedChanges
}) => {
  if (isPublished) {
    return false
  }
  if (preEnrollmentInvalid) {
    return false
  }
  return hasUnsavedChanges
}

/**
 * Whether the Networks save button should be enabled.
 * @param {Object} params
 * @param {string} params.name
 * @param {Array<object>} params.networkConfigs
 * @param {boolean} params.formNoErrors
 * @param {boolean} params.hasUnsavedChanges
 * @returns {boolean}
 */
export const resolveNetworksSaveEnabled = ({
  name,
  networkConfigs,
  formNoErrors,
  hasUnsavedChanges
}) => {
  const hasClientName = !!name
  const hasDirectoryType = !!networkConfigs?.[0]?.directoryType
  if (!hasClientName || !hasDirectoryType || !formNoErrors) {
    return false
  }
  return hasUnsavedChanges
}

/**
 * Whether the Save button should be enabled for the active Client Config tab.
 * @param {Object} params
 * @param {number} params.activeTab
 * @param {string} params.name
 * @param {Array<object>} params.networkConfigs
 * @param {boolean} params.formNoErrors
 * @param {boolean} params.hasUnsavedChanges
 * @param {boolean|undefined} params.isPublished
 * @param {boolean} params.preEnrollmentInvalid
 * @param {object|undefined} params.currentPlanAffiliations
 * @param {object|undefined} params.persistedPlanAffiliations
 * @returns {boolean}
 */
export const solveClientConfigSaveEnabled = ({
  activeTab,
  name,
  networkConfigs,
  formNoErrors,
  hasUnsavedChanges,
  isPublished,
  preEnrollmentInvalid,
  currentPlanAffiliations,
  persistedPlanAffiliations
}) => {
  if (activeTab === 0) {
    return resolveNetworksSaveEnabled({
      name,
      networkConfigs,
      formNoErrors,
      hasUnsavedChanges
    })
  }

  if (activeTab === 1) {
    return validatePreEnrollmentSaveEnabled({
      isPublished,
      preEnrollmentInvalid,
      hasUnsavedChanges
    })
  }

  if (activeTab === 2) {
    return resolveHasUnsavedChanges(
      parseComparablePlanAffiliationSaveState(currentPlanAffiliations),
      parseComparablePlanAffiliationSaveState(persistedPlanAffiliations)
    )
  }

  return false
}

/**
 * Resolves which save action the parent should take when Save is clicked.
 * @param {Object} params
 * @param {number} params.activeTab
 * @param {Array<{ includePreEnrollmentSite?: boolean }>} params.networkConfigs
 * @param {boolean|undefined} params.customContent
 * @returns {'open-save-network-modal'|'open-save-pre-enrollment-modal'|'save-direct'}
 */
export const resolveSaveModalAction = ({
  activeTab,
  networkConfigs,
  customContent
}) => {
  if (activeTab === 0) {
    const hasPreEnrollmentInclusion = networkConfigs.some((config) => config.includePreEnrollmentSite)
    if (!hasPreEnrollmentInclusion) {
      return SAVE_MODAL_ACTIONS.OPEN_SAVE_NETWORK_MODAL
    }
  }

  if (activeTab === 1) {
    if (customContent) {
      return SAVE_MODAL_ACTIONS.SAVE_DIRECT
    }
    return SAVE_MODAL_ACTIONS.OPEN_SAVE_PRE_ENROLLMENT_MODAL
  }

  return SAVE_MODAL_ACTIONS.SAVE_DIRECT
}

/**
 * Whether plan affiliation save should open the confirm modal.
 * @param {Array<{ networkAffiliation?: object }>} [clientPlansList]
 * @returns {boolean}
 */
export const resolveConfirmPlanAffiliationSave = (clientPlansList) =>
  clientPlansList?.some((plan) => !plan.networkAffiliation) ?? false

/**
 * Whether tab domain state must be saved before publish/unpublish.
 * @param {object} tabDomain
 * @param {object|undefined} clientData
 * @returns {boolean}
 */
export const resolvePreEnrollmentSubmitNeedsSave = (tabDomain, clientData) =>
  resolveHasUnsavedChanges(
    parseComparableClientConfig(tabDomain),
    parseComparableFromClientData(clientData)
  )

/**
 * Parses External/RBP rows on load by setting requireVpcModule=false when applicable.
 * @param {Array<object>} networkConfigs
 * @returns {Array<object>}
 */
export const parseExternalRbpConfigsOnLoad = (networkConfigs) =>
  networkConfigs.map((config) => {
    if (
      config &&
      config.directoryType === DIRECTORY_TYPES.EXTERNAL_RBP &&
      config.thirdPartyNetwork !== 'None' &&
      !config.requireVpcModule
    ) {
      return { ...config, requireVpcModule: false }
    }

    return config
  })

/**
 * Resolves plan type from store plans when planAffiliations is missing from loaded client data.
 * @param {Array<{ planId?: string }>|undefined} plans
 * @returns {string}
 */
export const resolvePlanTypeFromPlans = (plans) => {
  if (!plans) {
    return ''
  }
  const isHrpPlanType = plans.some((plan) => resolveIsHrpPlanId(plan?.planId, { match: 'prefix' }))
  return isHrpPlanType ? PLAN_TYPES.HRP : PLAN_TYPES.JAVELINA
}

/**
 * Maps default planAffiliations payload when missing from loaded client data.
 * @param {Array<{ planId?: string }>|undefined} plans
 * @returns {{ planType: string }}
 */
export const mapMissingPlanAffiliationsPayload = (plans) => ({
  planType: resolvePlanTypeFromPlans(plans)
})

/**
 * Maps base Pre-enrollment UI flags from domain fields.
 * @param {{ siteURL?: string, publish?: boolean }} preEnrollment
 * @returns {{ isOeUrlDisabled: boolean, isUrlValid: boolean, showUnpublishWarning: boolean }}
 */
export const mapPreEnrollmentUiBaseState = (preEnrollment) => ({
  isOeUrlDisabled: !!preEnrollment.siteURL,
  isUrlValid: !!preEnrollment.siteURL,
  showUnpublishWarning: preEnrollment.publish === true
})

/**
 * Maps Pre-enrollment UI state with URL check reset fields.
 * @param {{ isOeUrlDisabled: boolean, isUrlValid: boolean, showUnpublishWarning: boolean }} baseState
 * @returns {{ isOeUrlDisabled: boolean, isUrlValid: boolean, showUnpublishWarning: boolean, shouldShowUrlCheck: boolean, isUrlUnique: null }}
 */
export const mapPreEnrollmentUiStateWithResetCheck = (baseState) => ({
  ...baseState,
  shouldShowUrlCheck: false,
  isUrlUnique: null
})

/**
 * Derives Pre-enrollment UI state from pre-enrollment domain fields.
 * @param {{ siteURL?: string, publish?: boolean }} [preEnrollment]
 * @param {{ resetUrlCheck?: boolean }} [options]
 * @returns {{ isOeUrlDisabled: boolean, isUrlValid: boolean, showUnpublishWarning: boolean, shouldShowUrlCheck?: boolean, isUrlUnique?: null }}
 */
export const resolvePreEnrollmentUiState = (preEnrollment = {}, { resetUrlCheck = false } = {}) => {
  const baseState = mapPreEnrollmentUiBaseState(preEnrollment)
  if (!resetUrlCheck) {
    return baseState
  }
  return mapPreEnrollmentUiStateWithResetCheck(baseState)
}
